import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { LoaderCircle, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerOutline' | 'link'
export type ButtonSize = 'sm' | 'md'

const BASE =
  'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 ' +
  'focus-visible:ring-offset-1 focus-visible:ring-offset-background ' +
  'disabled:pointer-events-none aria-disabled:pointer-events-none'

/** A filled button that cannot be pressed turns into a flat grey block, the
 *  same in every colour — faded black or faded red read as "pressed" or
 *  "pink", not as "not now". Outlined ones just fade. */
const FILLED_OFF = 'disabled:bg-muted disabled:text-muted-foreground aria-disabled:bg-muted aria-disabled:text-muted-foreground'
const FADED_OFF = 'disabled:opacity-50 aria-disabled:opacity-50'

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn('bg-primary text-primary-foreground hover:bg-primary/85', FILLED_OFF),
  secondary: cn('border border-border bg-card text-foreground hover:border-foreground/20 hover:bg-muted', FADED_OFF),
  ghost: cn('text-foreground hover:bg-muted', FADED_OFF),
  /** The confirm button of a destructive dialog, a danger zone's one button. */
  danger: cn('bg-danger text-danger-foreground hover:bg-danger/90', FILLED_OFF),
  /** A destructive action that sits among others in a row or card (전량 회수,
   *  연락 금지): red, but not the loudest thing on the screen. */
  dangerOutline: cn(
    'border border-status-failed/35 bg-card text-status-failed hover:border-status-failed/60 hover:bg-status-failed-soft',
    FADED_OFF,
  ),
  link: cn('h-auto px-0 text-accent underline-offset-4 hover:underline', FADED_OFF),
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-table',
  md: 'h-9 px-3.5 text-body',
}

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string) {
  return cn(BASE, VARIANT[variant], variant === 'link' ? 'text-body' : SIZE[size], className)
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner and blocks clicks while an action runs. */
  loading?: boolean
  icon?: LucideIcon
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon: Icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClass(variant, size, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <LoaderCircle size={size === 'sm' ? 14 : 16} className="animate-spin" aria-hidden />
      ) : Icon ? (
        <Icon size={size === 'sm' ? 14 : 16} aria-hidden />
      ) : null}
      {children}
    </button>
  )
})

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: LucideIcon
  children?: ReactNode
}

/** A link that looks like a button — for "open the old page" and the like. */
export function ButtonLink({ variant = 'secondary', size = 'md', icon: Icon, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass(variant, size, typeof className === 'string' ? className : undefined)} {...rest}>
      {Icon && <Icon size={size === 'sm' ? 14 : 16} aria-hidden />}
      {children}
    </Link>
  )
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Read by screen readers and shown as the tooltip. */
  label: string
  icon: LucideIcon
  size?: ButtonSize
  variant?: Exclude<ButtonVariant, 'link'>
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon: Icon, size = 'md', variant = 'ghost', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(BASE, VARIANT[variant], size === 'sm' ? 'h-8 w-8' : 'h-9 w-9', 'px-0', className)}
      {...rest}
    >
      <Icon size={16} aria-hidden />
    </button>
  )
})
